// ═══════════════════════════════════════════════════════════════════════════
// PRINTER CONFIGURATION TYPES
// ═══════════════════════════════════════════════════════════════════════════

export enum PrinterType {
  THERMAL_58MM = "THERMAL_58MM",
  THERMAL_80MM = "THERMAL_80MM",
  DOT_MATRIX = "DOT_MATRIX",
  DOT_MATRIX_9_5X5_5 = "DOT_MATRIX_9_5X5_5",
  LASER_A4 = "LASER_A4",
  LASER_A5 = "LASER_A5",
}

export enum PrinterConnectionType {
  USB = "USB",
  SERIAL = "SERIAL",
  NETWORK = "NETWORK",
  SYSTEM = "SYSTEM",
}

export enum PrinterPurpose {
  BILLING = "BILLING",
  LABEL = "LABEL",
  REPORT = "REPORT",
}

/** USB connection configuration */
export interface USBConnectionConfig {
  vendorId: number;
  productId: number;
}

/** Serial connection configuration */
export interface SerialConnectionConfig {
  port: string;
  baudRate: number;
}

/** Network connection configuration */
export interface NetworkConnectionConfig {
  ip: string;
  networkPort: number;
}

/** System printer (OS-managed) configuration */
export interface SystemConnectionConfig {
  driverName?: string;
}

/** Union of all connection configurations */
export type PrinterConnectionConfig =
  | USBConnectionConfig
  | SerialConnectionConfig
  | NetworkConnectionConfig
  | SystemConnectionConfig;

/** Page margin settings in mm */
export interface PrintMargins {
  top: number;
  right: number;
  bottom: number;
  left: number;
}

/** Layout configuration that controls what and how to print */
export interface PrintLayoutConfig {
  // Typography
  fontSize: number;
  fontFamily: "monospace" | "sans-serif" | "serif";
  lineSpacing: number;
  // Margins (mm)
  margins: PrintMargins;
  // Section toggles
  showHeader: boolean;
  showStoreIdentity: boolean;
  showStoreContact: boolean;
  showStoreLicenses: boolean;
  showFooter: boolean;
  showTenders: boolean;
  showLogo: boolean;
  compactMode: boolean;
  // Data field toggles
  showGST: boolean;
  showDoctor: boolean;
  showHSN: boolean;
  showBatch: boolean;
  showMfr: boolean;
  showSKU: boolean;
  showSchedule: boolean;
  showKitIdentity: boolean;
  showKitComponents: boolean;
  showChargeDetails: boolean;
  showQuantity: boolean;
  showMRP: boolean;
  showExpiry: boolean;
  showDiscount: boolean;
  showTaxBreakdown: boolean;
  showPatientInfo: boolean;
  // Column control
  maxItemColumns: number;
  // Custom text
  headerLines: string[];
  footerLines: string[];
}

/** Complete printer configuration */
export interface StorePrinter {
  id: string;
  storeId: string;
  tenantId: string;
  name: string;
  printerType: PrinterType;
  connectionType: PrinterConnectionType;
  purpose: PrinterPurpose;
  paperWidth: number;
  paperHeight: number | null;
  connectionConfig: PrinterConnectionConfig | null;
  layoutConfig: PrintLayoutConfig | null;
  lastTestedAt?: string | null;
  lastTestStatus?: string | null;
  lastTestReference?: string | null;
  isDefault: boolean;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
  store?: { id: string; name: string };
}

/** Request to create a new printer */
export interface CreatePrinterRequest {
  storeId: string;
  tenantId?: string;
  name: string;
  printerType: PrinterType;
  connectionType?: PrinterConnectionType;
  purpose?: PrinterPurpose;
  paperWidth?: number;
  paperHeight?: number;
  connectionConfig?: PrinterConnectionConfig;
  layoutConfig?: Partial<PrintLayoutConfig>;
  isDefault?: boolean;
}

/** Request to update a printer */
export interface UpdatePrinterRequest {
  name?: string;
  printerType?: PrinterType;
  connectionType?: PrinterConnectionType;
  purpose?: PrinterPurpose;
  paperWidth?: number;
  paperHeight?: number;
  connectionConfig?: PrinterConnectionConfig;
  layoutConfig?: Partial<PrintLayoutConfig>;
  isDefault?: boolean;
  isActive?: boolean;
}

/** Human-readable printer type labels */
export const PRINTER_TYPE_LABELS: Record<PrinterType, string> = {
  [PrinterType.THERMAL_58MM]: "Thermal 58mm",
  [PrinterType.THERMAL_80MM]: "Thermal 80mm",
  [PrinterType.DOT_MATRIX]: "Dot Matrix",
  [PrinterType.DOT_MATRIX_9_5X5_5]: "Dot Matrix 9.5 x 5.5 in",
  [PrinterType.LASER_A4]: "Laser / Inkjet (A4)",
  [PrinterType.LASER_A5]: "Laser / Inkjet (A5)",
};

/** Exact physical paper dimensions in millimetres. Null height means continuous media. */
export const PRINTER_PAPER_DIMENSIONS: Record<
  PrinterType,
  { paperWidth: number; paperHeight: number | null }
> = {
  [PrinterType.THERMAL_58MM]: { paperWidth: 58, paperHeight: null },
  [PrinterType.THERMAL_80MM]: { paperWidth: 80, paperHeight: null },
  [PrinterType.DOT_MATRIX]: { paperWidth: 210, paperHeight: null },
  [PrinterType.DOT_MATRIX_9_5X5_5]: {
    paperWidth: 241.3,
    paperHeight: 139.7,
  },
  [PrinterType.LASER_A4]: { paperWidth: 210, paperHeight: 297 },
  [PrinterType.LASER_A5]: { paperWidth: 148, paperHeight: 210 },
};

/** Paper widths per printer type */
export const PRINTER_PAPER_WIDTHS: Record<PrinterType, number> = {
  [PrinterType.THERMAL_58MM]: 58,
  [PrinterType.THERMAL_80MM]: 80,
  [PrinterType.DOT_MATRIX]: 210,
  [PrinterType.DOT_MATRIX_9_5X5_5]: 241.3,
  [PrinterType.LASER_A4]: 210,
  [PrinterType.LASER_A5]: 148,
};

/** Connection type labels */
export const CONNECTION_TYPE_LABELS: Record<PrinterConnectionType, string> = {
  [PrinterConnectionType.USB]: "USB Direct",
  [PrinterConnectionType.SERIAL]: "Serial / COM Port",
  [PrinterConnectionType.NETWORK]: "Network (IP)",
  [PrinterConnectionType.SYSTEM]: "System Printer",
};

/** Purpose labels */
export const PRINTER_PURPOSE_LABELS: Record<PrinterPurpose, string> = {
  [PrinterPurpose.BILLING]: "Billing / Invoice",
  [PrinterPurpose.LABEL]: "Barcode / Label",
  [PrinterPurpose.REPORT]: "Reports / Statements",
};
